<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <a-alert
        type="info"
        show-icon
        style="margin-bottom: 16px"
        message="App 推送说明"
        description="全员推送：向所有已安装 App 的设备广播。通知推送会同时发送透传 payload 供 App 静默处理。指定用户推送：需 App 登录后上报个推 CID。"
      />

      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-form-item label="标题">
            <a-input v-model="queryParam.title" placeholder="筛选标题" allow-clear style="width: 180px" />
          </a-form-item>
          <a-form-item label="来源">
            <a-select v-model="queryParam.pushSource" placeholder="全部来源" allow-clear style="width: 160px">
              <a-select-option v-for="item in sourceOptions" :key="item.code" :value="item.code">
                {{ item.label }}
              </a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item>
            <a-button type="primary" @click="handleQuery"><a-icon type="search" />查询</a-button>
            <a-button style="margin-left: 8px" @click="resetQuery">重置</a-button>
            <a-button type="primary" style="margin-left: 16px" v-hasPermi="['giftcard:push:send']" @click="handleSend">
              <a-icon type="notification" />发送推送
            </a-button>
          </a-form-item>
        </a-form>
      </div>

      <a-table
        :loading="loading"
        rowKey="id"
        :columns="columns"
        :data-source="list"
        :pagination="false"
        :scroll="{ x: 1280 }"
      >
        <span slot="pushSource" slot-scope="text">
          <a-tag>{{ sourceLabel(text) }}</a-tag>
        </span>
        <span slot="targetType" slot-scope="text">
          <a-tag v-if="text === 1" color="blue">全部设备</a-tag>
          <a-tag v-else color="orange">指定用户</a-tag>
        </span>
        <span slot="platform" slot-scope="text">
          <span v-if="text === 'android'">Android</span>
          <span v-else-if="text === 'ios'">iOS</span>
          <span v-else>全平台</span>
        </span>
        <span slot="pushType" slot-scope="text">
          <a-tag v-if="text === 2">透传</a-tag>
          <a-tag v-else color="blue">通知+透传</a-tag>
        </span>
        <span slot="status" slot-scope="text">
          <a-tag v-if="text === 1" color="green">成功</a-tag>
          <a-tag v-else color="red">失败</a-tag>
        </span>
        <span slot="content" slot-scope="text">
          <span class="content-cell">{{ text }}</span>
        </span>
      </a-table>

      <a-pagination
        class="ant-table-pagination"
        :current="queryParam.pageNum"
        :total="total"
        :page-size="queryParam.pageSize"
        @change="changePage"
      />
    </a-card>

    <a-modal
      title="发送 App 推送"
      :visible="sendOpen"
      @ok="submitSend"
      @cancel="sendOpen = false"
      :confirmLoading="sendLoading"
      width="640px"
    >
      <a-form-model :model="sendForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="标题" required>
          <a-input v-model="sendForm.title" placeholder="通知标题" :maxLength="50" />
        </a-form-model-item>
        <a-form-model-item label="内容" required>
          <a-textarea v-model="sendForm.content" :rows="4" placeholder="通知正文" :maxLength="200" />
        </a-form-model-item>
        <a-form-model-item label="推送类型" required>
          <a-radio-group v-model="sendForm.pushType">
            <a-radio :value="1">通知（同时发透传）</a-radio>
            <a-radio :value="2">仅透传</a-radio>
          </a-radio-group>
          <div class="form-tip">通知会展示系统栏；透传供 App 前台静默接收 payload（如客服消息去重、跳转）</div>
        </a-form-model-item>
        <a-form-model-item label="推送平台">
          <a-radio-group v-model="sendForm.platform">
            <a-radio value="all">全平台</a-radio>
            <a-radio value="android">Android</a-radio>
            <a-radio value="ios">iOS</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item label="发送范围" required>
          <a-radio-group v-model="sendForm.targetType">
            <a-radio :value="1">全部设备</a-radio>
            <a-radio :value="2">指定用户</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item v-if="sendForm.targetType === 2" label="选择用户" required>
          <app-user-multi-select v-model="sendForm.userIds" />
          <div class="form-tip">用户需已在 App 端登录并上报个推 CID</div>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </page-header-wrapper>
</template>

<script>
import { pagePushRecord, sendPush, listPushSources } from '@/api/push/manage'
import AppUserMultiSelect from '@/components/AppUserMultiSelect'

export default {
  name: 'GiftcardPush',
  components: { AppUserMultiSelect },
  data() {
    return {
      loading: false,
      sendLoading: false,
      sendOpen: false,
      list: [],
      total: 0,
      sourceOptions: [],
      queryParam: { pageNum: 1, pageSize: 10, title: undefined, pushSource: undefined },
      sendForm: {
        title: '',
        content: '',
        pushType: 1,
        platform: 'all',
        targetType: 1,
        userIds: []
      },
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '标题', dataIndex: 'title', width: 160, ellipsis: true },
        { title: '来源', dataIndex: 'pushSource', scopedSlots: { customRender: 'pushSource' }, width: 120 },
        { title: '内容', dataIndex: 'content', scopedSlots: { customRender: 'content' }, width: 220 },
        { title: '类型', dataIndex: 'pushType', scopedSlots: { customRender: 'pushType' }, width: 110 },
        { title: '范围', dataIndex: 'targetType', scopedSlots: { customRender: 'targetType' }, width: 100 },
        { title: '平台', dataIndex: 'platform', scopedSlots: { customRender: 'platform' }, width: 90 },
        { title: '目标数', dataIndex: 'targetCount', width: 80 },
        { title: '成功数', dataIndex: 'successCount', width: 80 },
        { title: '状态', dataIndex: 'status', scopedSlots: { customRender: 'status' }, width: 80 },
        { title: '结果', dataIndex: 'resultMsg', width: 200, ellipsis: true },
        { title: '发送时间', dataIndex: 'createTime', width: 170 }
      ]
    }
  },
  created() {
    this.loadSources()
    this.getList()
  },
  methods: {
    loadSources() {
      listPushSources().then(res => {
        this.sourceOptions = res.data || []
      })
    },
    sourceLabel(code) {
      const item = this.sourceOptions.find(o => o.code === code)
      return item ? item.label : (code || '后台手动')
    },
    getList() {
      this.loading = true
      const params = { ...this.queryParam }
      if (!params.title) delete params.title
      if (!params.pushSource) delete params.pushSource
      pagePushRecord(params).then(res => {
        this.list = res.data.records || []
        this.total = res.data.total || 0
      }).finally(() => { this.loading = false })
    },
    handleQuery() {
      this.queryParam.pageNum = 1
      this.getList()
    },
    resetQuery() {
      this.queryParam = { pageNum: 1, pageSize: 10, title: undefined, pushSource: undefined }
      this.getList()
    },
    changePage(page, pageSize) {
      this.queryParam.pageNum = page
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    handleSend() {
      this.sendForm = {
        title: '',
        content: '',
        pushType: 1,
        platform: 'all',
        targetType: 1,
        userIds: []
      }
      this.sendOpen = true
    },
    submitSend() {
      if (!this.sendForm.title || !this.sendForm.title.trim()) {
        this.$message.warning('请输入标题')
        return
      }
      if (!this.sendForm.content || !this.sendForm.content.trim()) {
        this.$message.warning('请输入内容')
        return
      }
      if (this.sendForm.targetType === 2 && (!this.sendForm.userIds || this.sendForm.userIds.length === 0)) {
        this.$message.warning('请至少选择一个用户')
        return
      }
      const payload = {
        title: this.sendForm.title.trim(),
        content: this.sendForm.content.trim(),
        pushType: this.sendForm.pushType,
        platform: this.sendForm.platform,
        targetType: this.sendForm.targetType
      }
      if (this.sendForm.targetType === 2) {
        payload.userIdsText = this.sendForm.userIds.join(',')
      }
      const doSend = () => {
        this.sendLoading = true
        return sendPush(payload).then(res => {
          const data = res.data || {}
          const type = data.success ? 'success' : 'warning'
          this.$message[type](data.message || '推送已提交')
          this.sendOpen = false
          this.getList()
        }).finally(() => { this.sendLoading = false })
      }
      if (this.sendForm.targetType === 1) {
        this.$confirm({
          title: '确认向全部设备发送推送？',
          content: '将向所有已安装 App 的设备发送系统通知，请确认标题与内容无误。',
          onOk: doSend
        })
      } else {
        doSend()
      }
    }
  }
}
</script>

<style scoped>
.content-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 220px;
}
.form-tip {
  margin-top: 6px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  line-height: 1.5;
}
</style>
